// Matriz de planos do Forjamente — fonte da verdade do FRONTEND
// (mas o backend tem a sua própria em server/plans/gates.js — quem decide
// é sempre o backend; aqui é só pra UI antecipar gates e mostrar paywall).
//
// IDs internos: free / reader / leader / leader_pro
// Labels visíveis: Free / Leitor / Líder / Líder PRO
//
// 2 grupos pra organizar UI:
//   - GRUPO LEITORES   (reader): pra quem só lê — leitura ilimitada
//   - GRUPO CRIADORES  (leader, leader_pro): pra quem cria/lidera grupos
//
// reader_plus foi extinto: "Leitor" agora absorveu seus benefícios.
// Mantemos `reader_plus` no tipo como alias historico pra que payloads
// antigos (webhooks, sessions cacheadas) nao quebrem em runtime.

export type Plan = 'free' | 'reader' | 'reader_plus' | 'leader' | 'leader_pro';
export type PlanGroup = 'reader' | 'leader';
export type BillingPeriod = 'month' | 'year';
export type PaymentMethod = 'card' | 'pix';

export const PLAN_RANK: Record<Plan, number> = {
  free: 0,
  reader: 1,
  reader_plus: 1, // alias historico — mesmo nivel do reader
  leader: 2,
  leader_pro: 3,
};

export const PLANS_ORDERED: Plan[] = ['free', 'reader', 'leader', 'leader_pro'];

// ════════════════════════════════════════════════════════════════════
// PLANO ÚNICO — PREÇO DE LANÇAMENTO (R$4,90/mês · R$29,90/ano).
//
// O plano vendido é o SKU `leader`, e não porque ele seja "um plano menor":
// TODOS os planos pagos entregam o app inteiro, iguais (ver server/plans/
// gates.js). Os ids `reader`/`leader`/`leader_pro` são só ETIQUETAS DE PREÇO
// já cadastradas nas lojas — `leader` é a que está a R$4,90/R$29,90, que é o
// preço de lançamento. Mudar de preço no futuro = apontar SINGLE_PLAN para
// outro SKU já existente, sem repreçar produto na Apple/Google.
export const SINGLE_PLAN: Plan = 'leader';
export const SINGLE_PLAN_PRICE_YEAR = 29.90;
export const SINGLE_PLAN_PRICE_MONTH = 4.90;
/** Tarja do lançamento. Some trocando para ''. */
export const SINGLE_PLAN_BADGE = 'Preço de lançamento';
// Economia do anual vs 12 mensais (usado nos badges/copy do paywall).
export const SINGLE_PLAN_YEAR_SAVING = (SINGLE_PLAN_PRICE_MONTH * 12) - SINGLE_PLAN_PRICE_YEAR;
// ════════════════════════════════════════════════════════════════════

export const PLAN_NAMES: Record<Plan, string> = {
  free: 'Free',
  reader: 'Leitor',
  reader_plus: 'Leitor', // alias
  // Todos os pagos entregam o mesmo app. O nome comercial é um só; os
  // antigos ficam para não quebrar telas de assinantes legados.
  leader: 'Forjamente Completo',     // plano único — preço de lançamento
  leader_pro: 'Forjamente Completo', // assinantes legados, mesmo acesso
};

export const PLAN_TAGLINES: Record<Plan, string> = {
  free: 'Pra começar',
  reader: 'Liberdade total de leitura',
  reader_plus: 'Liberdade total de leitura', // alias
  leader: 'Pra liderar grupos',
  leader_pro: 'O Forjamente inteiro, sem amarras',
};

// Qual grupo o plano pertence (organiza UI em 2 seções)
export const PLAN_GROUP: Record<Plan, PlanGroup> = {
  free: 'reader',
  reader: 'reader',
  reader_plus: 'reader',
  leader: 'leader',
  leader_pro: 'leader',
};

// Preços fallback. O frontend lê os reais via /api/plans (admin pode editar
// no console). Se o fetch falhar, cai pros valores aqui.
export const PLAN_PRICES: Record<Plan, {
  month: number;
  year: number;
  monthMethods: PaymentMethod[];
  yearMethods: PaymentMethod[];
  discountLabel?: string | null;
} | null> = {
  // Planos antigos foram descontinuados no relançamento (plano único). Preço
  // null = não têm vitrine e não podem ser cotados em lugar nenhum. Os IDs
  // continuam existindo só pra não quebrar payloads/webhooks legados.
  free:        null,
  reader:      null,
  reader_plus: null,
  leader_pro:  null,   // assinantes legados mantêm o acesso; não está à venda
  leader: {
    // PLANO ÚNICO, dois jeitos de pagar:
    //   anual R$29,90 (pagamento único, cartão ou Pix à vista)
    //   mensal R$4,90 (assinatura recorrente, só cartão)
    month: SINGLE_PLAN_PRICE_MONTH,
    year: SINGLE_PLAN_PRICE_YEAR,
    monthMethods: ['card'],
    yearMethods: ['card', 'pix'],
    discountLabel: null,
  },
};

// ────────────────────────────────────────────────────────────────────
// Tabela de gates do FRONTEND (espelho do server/plans/gates.js).
// O backend tem a verdade final — esses valores aqui são só pra que a UI
// possa esconder/desabilitar coisas ANTES do user tentar a ação.
// ────────────────────────────────────────────────────────────────────
export interface PlanGates {
  // Livros simultâneos com status='lendo'. User troca livremente, mas o total
  // não pode passar do teto (free=2, reader=∞).
  maxConcurrentBooks: number;
  aiAnalysis: boolean;
  maxClubsJoined: number;
  canExploreClubs: boolean;
  maxPrivateClubsCreated: number;
  maxPublicClubsCreated: number;
  maxPrivateClubMembers: number;
  maxPublicClubMembers: number;
  leaderTools: boolean;
}

// O app inteiro. TODO plano pago entrega exatamente isto — os ids
// reader/leader/leader_pro são etiquetas de PREÇO, não níveis de acesso.
// Espelha server/plans/gates.js: se mudar lá, muda aqui.
const ACESSO_PAGO: PlanGates = {
  maxConcurrentBooks: Infinity,
  aiAnalysis: true,
  maxClubsJoined: Infinity,
  canExploreClubs: true,
  maxPrivateClubsCreated: Infinity,
  maxPublicClubsCreated: Infinity,
  maxPrivateClubMembers: Infinity,
  maxPublicClubMembers: Infinity,
  leaderTools: true,
};

// O app é grátis (Wesley, 14/10/2026): o free tem o app inteiro. Criar clube
// é só para Mentor — isso vem de profile.is_mentor, não do plano.
export const PLAN_GATES: Record<Plan, PlanGates> = {
  free: ACESSO_PAGO,
  reader: ACESSO_PAGO,
  reader_plus: ACESSO_PAGO,
  leader: ACESSO_PAGO,
  leader_pro: ACESSO_PAGO,
};

export function gatesFor(plan: Plan | undefined | null): PlanGates {
  return PLAN_GATES[plan || 'free'];
}

// Features destacadas por plano (UX do paywall). Cada plano lista o que ELE
// adiciona em relação ao anterior — pra deixar clara a progressão.
// Features destacadas POR PLANO no paywall.
// Espelham 1:1 a Tabela Oficial Forjamente — sem benefícios genéricos.
// O placeholder `{term}` é substituído em runtime pelo termo preferido
// do usuário ('grupo' ou 'clube' — vem de profile.preferred_term).
const ACESSO_PAGO_FEATURES: string[] = [
  'Leitura sem teto — quantos livros quiser, ao mesmo tempo',
  'O tracker que te puxa de volta — chama, sequência, páginas e metas',
  'O Protocolo do Hábito — o passo a passo pra leitura virar rotina que fica',
  // Termo no meio da frase de propósito: {term} vem minúsculo do
  // applyTermInFeature e ficaria com inicial minúscula no começo da linha.
  'Sem limite de {term}s — entre, crie e lidere, público ou privado',
  'Ferramentas de líder — analytics, marcos e caixa de perguntas',
];

export const PLAN_FEATURES: Record<Plan, string[]> = {
  free: [
    '2 livros lendo simultâneos ({term}s contam)',
    'Tracker completo: chama, streak, registro de páginas e metas',
    'Participar de até 2 {term}s',
    'Criar 1 {term} privado (máx 10 membros por {term})',
    'Análise com IA',
  ],
  // Uma lista só: todo plano pago entrega isto. Se você está prestes a
  // escrever "até N" em alguma destas linhas, pare — não existe teto por plano.
  reader: ACESSO_PAGO_FEATURES,
  reader_plus: ACESSO_PAGO_FEATURES,
  leader: ACESSO_PAGO_FEATURES,
  leader_pro: ACESSO_PAGO_FEATURES,
};

// Substitui placeholders `{term}` e `{term}s` pelo termo escolhido pelo user.
// Default = "clube" se profile não tem preferred_term setado.
export function applyTermInFeature(feature: string, preferredTerm?: string | null): string {
  const term = (preferredTerm === 'grupo' || preferredTerm === 'clube') ? preferredTerm : 'clube';
  return feature
    .replace(/\{term\}s/g, term + 's')
    .replace(/\{term\}/g, term);
}

// Mapeia features → plano mínimo necessário. Usado pra que o paywall mostre o
// "nível mínimo" certo na hora que user tenta a ação.
export const FEATURE_REQUIREMENTS: Record<string, Plan> = {
  // Pessoal — tudo cai no reader agora (que absorveu o reader_plus)
  more_concurrent_books:      SINGLE_PLAN,
  unlimited_concurrent_books: SINGLE_PLAN,
  insights_ai:                SINGLE_PLAN,
  personal_analytics:         SINGLE_PLAN,
  // Grupos — participar
  more_clubs_joined:        SINGLE_PLAN,
  unlimited_clubs_joined:   SINGLE_PLAN,
  explore_clubs:            SINGLE_PLAN,
  // Grupos — criar privados
  more_private_clubs:       SINGLE_PLAN,
  unlimited_private_clubs:  SINGLE_PLAN,
  // Grupos — criar públicos
  create_public_club:       SINGLE_PLAN,
  more_public_clubs:        SINGLE_PLAN,
  unlimited_public_members: SINGLE_PLAN,
  unlimited_public_clubs:   SINGLE_PLAN,
  // Ferramentas de líder
  leader_tools:             SINGLE_PLAN,
  club_dashboard:           SINGLE_PLAN,
  question_box:             'leader',
  insight_final:            'leader',
};

export function getPlanRank(plan: Plan | undefined | null): number {
  return PLAN_RANK[plan || 'free'] ?? 0;
}

export function meetsPlan(profile: { plan?: Plan | null } | null | undefined, required: Plan): boolean {
  return getPlanRank(profile?.plan as Plan) >= getPlanRank(required);
}

export function planForFeature(featureId: string): Plan {
  return FEATURE_REQUIREMENTS[featureId] || 'reader';
}

// Helper de display: formata BRL
export function formatBRL(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

// Calcula economia mensal equivalente quando user opta pelo anual
export function monthlyEquivalent(plan: Plan): number | null {
  const p = PLAN_PRICES[plan];
  if (!p) return null;
  return p.year / 12;
}

/** Quem tem o app inteiro SEM assinatura, e até quando (pente fino, UX-10/QA-06).
 *  `profile.plan` continua 'free' durante o teste grátis, o ano por indicação e
 *  para fundadores — e as telas mostravam "Free" / "Sem assinatura" como se o
 *  app estivesse travado. Mesma regra do App.tsx (isPro) e do servidor
 *  (plans/gates.js planoEfetivo). */
export function acessoSemAssinatura(profile: any): { tipo: 'fundador' | 'indicacao' | 'gratis'; ate: string | null } | null {
  if (!profile) return null;
  const agora = Date.now();
  if (profile.is_founder) return { tipo: 'fundador', ate: null };
  if (profile.promo_access_until && new Date(profile.promo_access_until).getTime() > agora) {
    return { tipo: 'indicacao', ate: profile.promo_access_until };
  }
  // Desde 14/10/2026 o app é grátis: sem assinatura = app inteiro, sem prazo.
  // O teste de 7 dias acabou (trial_ends_at ficou só como histórico).
  return { tipo: 'gratis', ate: null };
}

/** "App grátis" / "Acesso por indicação até 9 de out." — o rótulo curto. */
export function rotuloDoAcesso(a: ReturnType<typeof acessoSemAssinatura>): string | null {
  if (!a) return null;
  const data = a.ate ? new Date(a.ate).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }).replace('.', '') : '';
  if (a.tipo === 'fundador') return 'Fundador · acesso completo';
  if (a.tipo === 'indicacao') return `Acesso por indicação até ${data}`;
  return 'App grátis · tudo liberado';
}
